import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Loader2, Send, X } from "lucide-react";
import { createPost, editPost } from "../../services/api";
import { optimizeImage } from "../../utils/optimizeImage";

// HAPPYCAP_POST_FORM_HARMONY_V1
const PostForm = ({ postToEdit = null, onSuccess }) => {
  const { t } = useTranslation();

  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);
  const [textWarning, setTextWarning] = useState(false);

  useEffect(() => {
    if (postToEdit) {
      setText(postToEdit.text || "");
      setImage(null);
      setImagePreview(postToEdit.image_url || null);
    }
  }, [postToEdit]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setImage(null);
    setImagePreview(null);

    try {
      const optimized = await optimizeImage(file);
      setImage(optimized);
      setImagePreview(URL.createObjectURL(optimized));
    } catch (err) {
      console.error("Error al optimizar imagen", err);
      setError(
        t("post.form.image_error", "No se pudo procesar la fotografía.")
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return;

    if (!text.trim()) {
      setTextWarning(true);
      textInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      textInputRef.current?.focus({ preventScroll: true });
      return;
    }

    setError("");
    setSubmitting(true);

    const formData = new FormData();
    formData.append("text", text);

    if (image) formData.append("image", image);

    try {
      const response = postToEdit
        ? await editPost(postToEdit.id, formData)
        : await createPost(formData);

      if (onSuccess) onSuccess(response.data);

      if (!postToEdit) {
        setText("");
        setImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Error al enviar post", err);
      setError(
        err.response?.data?.detail ||
        "No se pudo guardar la publicación. Inténtalo de nuevo."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div>

        <textarea
          ref={textInputRef}
          id="post-text"
          aria-label={t("post.form.placeholder")}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (e.target.value.trim()) setTextWarning(false);
          }}
          aria-invalid={textWarning}
          aria-describedby={textWarning ? "post-text-warning" : undefined}
          rows={4}
          className="block min-h-[110px] w-full resize-y rounded-2xl border border-[#C7DDEB] bg-[#F8FBFE] px-4 py-4 text-lg leading-[1.6] text-slate-800 placeholder:text-slate-400 focus:border-[#247BA8] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#247BA8]/20"
        />
        {textWarning && (
          <p
            id="post-text-warning"
            role="alert"
            className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800"
          >
            {t("post.form.text_required", "Escribe un texto antes de publicar.")}
          </p>
        )}
      </div>

      <div className="flex flex-col items-center gap-3">
        <label
          htmlFor="post-image"
          title={imagePreview ? t("post.form.change") : t("post.form.upload")}
          aria-label={imagePreview ? t("post.form.change") : t("post.form.upload")}
          className="inline-flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border border-[#D8E7F0] bg-[#EFF7FC] text-[#247BA8] shadow-sm transition hover:border-[#B8D8EA] hover:bg-[#E2F1FA] hover:shadow-md focus-within:ring-2 focus-within:ring-[#247BA8]/20"
        >
          <Camera size={25} strokeWidth={1.8} aria-hidden="true" />
        </label>

        <input
          ref={fileInputRef}
          id="post-image"
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="sr-only"
        />

        {imagePreview && (
          <div className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-2">
            <img
              src={imagePreview}
              alt={t("post.form.preview_alt")}
              className="mx-auto max-h-[160px] w-full rounded-xl object-contain sm:max-h-[240px]"
            />

            <button
              type="button"
              onClick={handleRemoveImage}
              aria-label={t("common.close")}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-700 shadow-md hover:text-red-600"
            >
              <X size={19} />
            </button>
          </div>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mx-auto flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[#247BA8] px-7 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#19658E] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#247BA8]/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
      >
        {submitting ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <Send size={18} />
        )}

        <span>
          {submitting
            ? t("post.form.publishing")
            : postToEdit
            ? t("post.form.update")
            : t("post.form.publish")}
        </span>
      </button>
    </form>
  );
};

export default PostForm;
